// Ranked record results for the shared desktop/mobile search. Matching, authorization and
// pagination belong to POST /api/search/records; this session owns presentation and cancellation.
import { humanizeToken } from '@affine/erp-ui';
import {
  DatabaseTableViewIcon,
  DeleteIcon,
  DocsIcon,
  FileIcon,
  LockIcon,
  MemberIcon,
  SaveIcon,
  ToolIcon,
  UserIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import { Entity, LiveData } from '@toeverything/infra';

import type { ErpService, RecordRow } from '../../erp';
import { erpSiteScope$ } from '../../erp/erp-site-scope';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchItem } from '../types/item';
import { type ErpPayload, isLegacyRow } from './erp-route';
import { createPagedSearch } from './paged-search';

// Re-export the pure routing SSOT so existing importers (services/cmdk.ts) keep a single import site.
export { type ErpPayload, erpResultRoute, isLegacyRow } from './erp-route';

// A small, design-system (line-icon) map per entity type; everything else falls back to a generic
// record icon. All names are verified exports of @blocksuite/icons/rc.
const TYPE_ICONS: Record<string, typeof DatabaseTableViewIcon> = {
  account: MemberIcon,
  contact: UserIcon,
  employee: UserIcon,
  drive: SaveIcon,
  asset: ToolIcon,
  invoice: FileIcon,
  document: DocsIcon,
  destruction_certificate: FileIcon,
  data_destruction_record: DeleteIcon,
  quarantine_record: LockIcon,
  facility: ViewLayersIcon,
};

// Prefer useful visible business values; omit technical references and redaction markers.
function erpSubtitle(row: RecordRow): string {
  const preferred = [
    'tag_id',
    'serial_number',
    'barcode',
    'order_no',
    'manufacturer',
    'model',
    'name',
    'state',
  ];
  return [
    humanizeToken(row.type),
    ...preferred
      .flatMap(key => {
        const value = row.data[key];
        return !row.data[`${key}_redacted`] &&
          (typeof value === 'string' || typeof value === 'number') &&
          String(value) !== row.title
          ? [String(value).slice(0, 64)]
          : [];
      })
      .slice(0, 3),
  ].join(' · ');
}

export class ErpQuickSearchSession
  extends Entity
  implements QuickSearchSession<'erp', ErpPayload>
{
  constructor(private readonly erpService: ErpService) {
    super();
    const subscription = erpSiteScope$.subscribe(() =>
      this.search.query(this.currentQuery)
    );
    this.disposables.push(() => subscription.unsubscribe());
  }

  items$ = new LiveData<QuickSearchItem<'erp', ErpPayload>[]>([]);
  isLoading$ = new LiveData(false);
  error$ = new LiveData<unknown>(null);
  hasMore$ = new LiveData(false);
  private readonly search = createPagedSearch<RecordRow>({
    search: (query, offset, signal) =>
      this.erpService
        .searchRecords(query, { limit: 30, offset, signal })
        .catch((error: unknown) => {
          // A docs-only user's palette has no ERP section. The server's verdict is authoritative;
          // never derive a second ERP role list in the client.
          if (
            error instanceof Error &&
            'status' in error &&
            error.status === 403
          )
            return { rows: [], has_more: false, next_offset: null };
          throw error;
        }),
    onChange: state => {
      this.items$.next(
        state.rows.map((row, i) => this.mapRow(row, state.query, i))
      );
      this.isLoading$.next(state.loading);
      this.hasMore$.next(state.hasMore);
      this.error$.next(state.error);
    },
  });

  private mapRow(
    row: RecordRow,
    query: string,
    index: number
  ): QuickSearchItem<'erp', ErpPayload> {
    const typeLabel = humanizeToken(row.type);
    // Fail-closed: a redaction flag wins over any title value (localized placeholder via i18nKey).
    const title = row.title_redacted
      ? { i18nKey: 'com.affine.pipe.records.redacted' as const }
      : (row.title ?? `${typeLabel} ${row.id.slice(0, 8)}`);
    return {
      id: `erp:${row.id}`,
      source: 'erp',
      group: {
        // Keep one ordered group: grouping by type would move weaker matches above exact ones.
        id: 'erp',
        label: { i18nKey: 'com.affine.pipe.search.records' },
        score: 6,
      },
      label: { title, subTitle: erpSubtitle(row) || undefined },
      // Preserve server relevance across all entity types.
      score: 1000 - index,
      note:
        row.search_match === 'near'
          ? { i18nKey: 'com.affine.pipe.search.near' }
          : undefined,
      icon: TYPE_ICONS[row.type] ?? DatabaseTableViewIcon,
      payload: {
        type: row.type,
        id: row.id,
        stage: row.stage ?? null,
        seed: query,
        legacy: isLegacyRow(row.data),
      },
    };
  }

  private currentQuery = '';
  query = (query: string) => {
    this.currentQuery = query;
    this.search.query(query);
  };
  loadMore = () => this.search.loadMore();

  override dispose(): void {
    this.search.dispose();
    super.dispose();
  }
}
